import wazzupApi from "../wazzupApi";

const BITRIX_EVENT_TYPES = [
  { typeId: -3, titleRU: "Рабочее время", titleEN: "work_time" },
  { typeId: -2, titleRU: "Переписка", titleEN: "correspondence" },
  { typeId: 101, titleRU: "CRM-активности", titleEN: "crm_activity" },
  { typeId: 102, titleRU: "Задачи", titleEN: "tasks" },
  { typeId: 103, titleRU: "Сделки", titleEN: "deals" },
  { typeId: 104, titleRU: "CRM-история", titleEN: "crm_history" },
  { typeId: 105, titleRU: "Смена стадии", titleEN: "crm_stage" },
  { typeId: 106, titleRU: "Комментарии CRM", titleEN: "crm_comments" },
  { typeId: 107, titleRU: "Лиды", titleEN: "leads" },
  { typeId: 108, titleRU: "Контакты", titleEN: "contacts" },
  { typeId: 109, titleRU: "Компании", titleEN: "companies" },
  { typeId: 110, titleRU: "Смарт-процессы", titleEN: "smart_processes" },
  { typeId: 111, titleRU: "Комментарии задач", titleEN: "task_comments" },
  { typeId: 112, titleRU: "Время в задачах", titleEN: "task_time" },
  { typeId: 113, titleRU: "Почта", titleEN: "email" },
  { typeId: 201, titleRU: "Звонки", titleEN: "calls" },
];

const DEFAULT_SETTINGS = {
  subdomain: "",
  timezone: "Europe/Moscow",
  workingHours: "08:00-20:00",
  customWorkingTime: false,
  showTotalInTime: false,
  customEventsEnabled: false,
  maxMinutesBetweenEventsCountAsWork: 5,
  NOT_UNION_EVENT_TYPES_MAP: {
    "-2": true,
    201: true,
  },
};

const getPublicToken = () => {
  const match = window.location.pathname.match(/^\/p\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

const getBX24 = () => window.BX24 || null;

let portalSessionPromise = null;
let portalSessionPayload = null;

const initBX24 = () =>
  new Promise((resolve) => {
    const BX24 = getBX24();

    if (!BX24 || typeof BX24.init !== "function") {
      resolve(BX24);
      return;
    }

    BX24.init(() => resolve(BX24));
  });

const getBitrixDomain = () => {
  const BX24 = getBX24();

  if (BX24 && typeof BX24.getDomain === "function") {
    return BX24.getDomain();
  }

  return window.location.hostname;
};

const getMemberId = () => {
  try {
    const BX24 = getBX24();
    const auth = BX24 && typeof BX24.getAuth === "function"
      ? BX24.getAuth()
      : null;

    return auth && auth.member_id ? auth.member_id : "";
  } catch (error) {
    return "";
  }
};

const getLocationPayload = () =>
  Object.fromEntries(new URLSearchParams(window.location.search));

const getPortalQuery = (extra = {}) =>
  new URLSearchParams({
    domain: getBitrixDomain(),
    memberId: getMemberId(),
    ...extra,
  }).toString();

const requestJson = async (url) => {
  const response = await fetch(url, { credentials: "include" });

  if (!response.ok) {
    return null;
  }

  return response.json();
};

const postJson = async (url, payload) => {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    return null;
  }

  return response.json();
};

const ensurePortalSession = async () => {
  if (getPublicToken()) {
    return null;
  }

  if (portalSessionPromise) {
    return portalSessionPromise;
  }

  portalSessionPromise = (async () => {
    const BX24 = await initBX24();
    const locationPayload = getLocationPayload();

    if (!BX24 && !Object.keys(locationPayload).length) {
      return null;
    }

    const auth = BX24 && typeof BX24.getAuth === "function"
      ? BX24.getAuth() || {}
      : {};
    const domain = BX24 && typeof BX24.getDomain === "function" ? BX24.getDomain() : "";
    const payload = {
      ...locationPayload,
      ...auth,
      debugSource: "b24-frontend-session",
      domain: auth.domain || auth.DOMAIN || locationPayload.domain || locationPayload.DOMAIN || domain,
      member_id:
        auth.member_id ||
        auth.MEMBER_ID ||
        locationPayload.member_id ||
        locationPayload.memberId ||
        locationPayload.MEMBER_ID ||
        "",
    };

    if (!payload.access_token && !payload.AUTH_ID && !payload.auth_id && !payload.refresh_token && !payload.REFRESH_ID) {
      payload.noAuthToken = true;
    }

    if (!Object.keys(payload).length) {
      return null;
    }

    portalSessionPayload = payload;
    return postJson("/api/portal/session", payload);
  })();

  return portalSessionPromise;
};

const normalizeFilterSet = (values) => {
  if (!Array.isArray(values) || !values.length) {
    return null;
  }

  return new Set(values.map((value) => String(value)));
};

const filterEvents = (eventsByUser, query = {}) => {
  const userFilter = normalizeFilterSet(query.users);
  const typeFilter = normalizeFilterSet(query.eventTypes);
  const filtered = {};

  Object.keys(eventsByUser || {}).forEach((userId) => {
    if (userFilter && !userFilter.has(String(userId))) {
      return;
    }

    filtered[userId] = (eventsByUser[userId] || []).filter((event) => {
      const typeId = String(event.typeId);

      return typeId === "-3" || !typeFilter || typeFilter.has(typeId);
    });
  });

  return filtered;
};

const getWazzupAuth = async () => {
  const publicToken = getPublicToken();

  if (publicToken) {
    return {
      provider: "b24",
      publicToken,
    };
  }

  await ensurePortalSession();
  return {
    provider: "b24",
    ...(portalSessionPayload || {}),
  };
};

const getPublicOrBackendUrl = (publicPath, backendPath, extra = {}) => {
  const publicToken = getPublicToken();

  if (publicToken) {
    return `${publicPath}?${new URLSearchParams({
      token: publicToken,
      ...extra,
    })}`;
  }

  return `${backendPath}?${getPortalQuery(extra)}`;
};

export default {
  provider: "b24",

  getStorageScope() {
    return getPublicToken() || getMemberId() || getBitrixDomain() || "b24";
  },

  async getLookup() {
    await ensurePortalSession();

    const url = getPublicOrBackendUrl(
      "/api/public/lookup",
      "/api/backend/lookup"
    );
    const data = await requestJson(url);

    if (!data || !data.ok) {
      throw new Error("Список сотрудников Bitrix24 пока недоступен");
    }

    return {
      usersByDeps: data.usersByDeps || [],
      eventTypes: BITRIX_EVENT_TYPES,
    };
  },

  async getDayEvents(day, query = {}) {
    await ensurePortalSession();

    const url = getPublicOrBackendUrl(
      "/api/public/day",
      "/api/backend/day",
      { day }
    );
    const [data, wazzupData] = await Promise.all([
      requestJson(url),
      getWazzupAuth().then((auth) =>
        wazzupApi.getDaySafe(auth, day, DEFAULT_SETTINGS.timezone)
      ),
    ]);

    if (!data || !data.ok || !data.events) {
      throw new Error("Данные Bitrix24 за выбранный день еще не синхронизированы");
    }

    const mergedEvents = wazzupApi.mergeDayEvents({
      crmEvents: data.events,
      wazzupData,
      isCrmMessageEvent: (event) => Number(event.typeId) === -2,
    });

    return filterEvents(mergedEvents, query);
  },

  async getSettings() {
    return {
      ...DEFAULT_SETTINGS,
      subdomain: getBitrixDomain(),
    };
  },

  async getPortalStatus() {
    await ensurePortalSession();

    const publicToken = getPublicToken();
    const url = publicToken
      ? `/api/public/status?${new URLSearchParams({ token: publicToken })}`
      : `/api/portal/status?${getPortalQuery()}`;

    return requestJson(url);
  },

  async getWazzupConfig(options) {
    return wazzupApi.getConfig(await getWazzupAuth(), options);
  },

  async connectWazzup(apiKey) {
    return wazzupApi.connect(await getWazzupAuth(), apiKey);
  },

  async saveWazzupMappings(mappings) {
    return wazzupApi.saveMappings(await getWazzupAuth(), mappings);
  },
};
